'use client';

import type {
  ComponentVisibility,
  FormComponent,
  FormDSLContext,
  ValidationRules,
} from '@commenda/types';
import { createContext, type Dispatch, type SetStateAction } from 'react';

export interface FormContextType {
  formData: Record<string, unknown>;
  setFormData: Dispatch<SetStateAction<Record<string, unknown>>>;
  updateField: (fieldName: string, value: unknown) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
  validateField: (
    fieldName: string,
    value: unknown,
    rules?: ValidationRules
  ) => string | null;
  isFieldVisible: (visibility?: ComponentVisibility) => boolean;
  isFieldDisabled: (fieldName?: string) => boolean;
  /** DSL context for Commenda OS operations */
  dslContext?: FormDSLContext;
  /**
   * Current form template id when known (e.g. in preview).
   * Used to hide NestedForm when the linked form is the same as the current form.
   */
  formTemplateId?: string;
  /**
   * Version-agnostic key string for the current form template (e.g. "my-form").
   * Used as a fallback self-reference check for NestedForm when the current
   * template is a draft (formTemplateId differs from the key's liveFormId).
   */
  formTemplateKey?: string;
  /**
   * Which nested form drawer is open (fieldId + add vs edit index).
   * Persisted so after refresh/close the same nested form reopens.
   */
  nestedFormOpen: { fieldId: string; editingIndex: number | null } | null;
  setNestedFormOpen: (
    state: { fieldId: string; editingIndex: number | null } | null
  ) => void;
  /** Track which fields are currently uploading files */
  uploadingFields: Set<string>;
  setFieldUploading: (fieldId: string, isUploading: boolean) => void;
  /** Map of fieldId -> FormComponent for looking up component definitions */
  componentMap?: Record<string, FormComponent>;
  /**
   * Register a callback that runs once on form submit, after the standard
   * Commenda OS field writes. For composite nodes whose value maps to multiple
   * DSL writes (e.g. Ownership → many shareholdings). Keyed by id, so a node
   * re-registers on value change and the latest closure wins.
   *
   * What it resolves with is the value saved for THIS field, not a map of fields — the renderer
   * keys it by the id registered here. A node that learned something during its writes (an id
   * the backend minted, say) returns the value carrying it, so the saved response matches what
   * was actually written. Resolving with nothing keeps whatever is in form state.
   */
  registerSubmitHandler?: (id: string, handler: () => Promise<unknown>) => void;
  /**
   * A component that finds nothing to ask this entity (an identifier its jurisdiction does not
   * issue, say) renders nothing; marking its field here drops the field's required check, so no
   * step waits on a question nobody can see.
   */
  setFieldNotApplicable?: (fieldId: string, notApplicable: boolean) => void;
  /**
   * Preview-only: register how this component fills itself with a test value.
   * Components whose values are selections from data only they have loaded
   * (key persons, bank accounts) or whose value shapes they alone own
   * (locations, agents) register here; the preview Fill button invokes the
   * handler instead of guessing a value from outside. Pass null to unregister
   * (on unmount). Keyed by fieldId; latest registration wins.
   */
  registerPreviewFillHandler?: (
    fieldId: string,
    handler: PreviewFillHandler | null
  ) => void;
  /**
   * When true, the form is being rendered in preview mode.
   * All write operations (backend saves, Commenda OS writes, bank account creation, etc.)
   * are skipped so preview never mutates real data.
   */
  isPreview?: boolean;
  hasAttemptedSubmit?: boolean;
}

/**
 * Outcome of a component filling itself in preview. `loading` means the
 * component's data hasn't resolved yet — the run-to-end tester retries the
 * step briefly on this outcome, while the manual Fill button reports it as a
 * try-again-shortly skip.
 */
export type PreviewFillOutcome =
  | { kind: 'filled' }
  | { kind: 'already-filled' }
  | { kind: 'loading' }
  | { kind: 'skipped'; reason: string };

export type PreviewFillHandler = () => PreviewFillOutcome;

export const FormContext = createContext<FormContextType>({
  formData: {},
  setFormData: () => {},
  updateField: () => {},
  currentStep: 0,
  setCurrentStep: () => {},
  errors: {},
  setErrors: () => {},
  validateField: () => null,
  isFieldVisible: () => true,
  isFieldDisabled: () => false,
  dslContext: undefined,
  formTemplateId: undefined,
  formTemplateKey: undefined,
  nestedFormOpen: null,
  setNestedFormOpen: () => {},
  uploadingFields: new Set<string>(),
  setFieldUploading: () => {},
  componentMap: undefined,
  registerSubmitHandler: () => {},
  setFieldNotApplicable: () => {},
  registerPreviewFillHandler: () => {},
});
